import { Center, Text, VStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { useRef, useState } from 'react';
import { number, object } from 'yup';

import Modal from '../../../../../../components/Modal';
import { NumberInput } from '../../../../../../components/formComponents';
import Button from '../../../../../../components/ui/buttons/Button';
import PrimaryButton from '../../../../../../components/ui/buttons/PrimaryButton';
import { getLeadByExternalIdQueryKey } from '../../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalId';
import { usePatchLeadByExternalIdData } from '../../../../../../services/marketplace/generated/kubb/hooks/usePatchLeadByExternalIdData';
import type { GetLeadByExternalId200 } from '../../../../../../services/marketplace/generated/kubb/models/GetLeadByExternalId';
import { formatMileage } from '../../../../../../utils/helpers';

// No upper bound: unlike the buyout flow's 150k financing cap, an accepted
// sale takes the reading as-is.
const odometerSchema = object({
  mileage: number()
    .typeError('Enter your current mileage.')
    .required('Enter your current mileage.')
    .integer('Enter whole miles, no decimals.')
    .positive('Enter your current mileage.'),
});

type OdometerMileageFormValues = {
  mileage: number | '';
};

type OdometerMileageModalProps = {
  isOpen: boolean;
  externalId: string;
  onConfirm: () => void;
  onCancel: () => void;
};

const OdometerMileageModal = ({
  isOpen,
  externalId,
  onConfirm,
  onCancel,
}: OdometerMileageModalProps) => {
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showBelowBaselineWarning, setShowBelowBaselineWarning] = useState(false);
  const confirmedBelowBaseline = useRef(false);
  const queryClient = useQueryClient();
  const { mutateAsync: patchLead, isPending } = usePatchLeadByExternalIdData();

  const save = async (values: OdometerMileageFormValues) => {
    setSaveError(null);
    try {
      const updatedLead = await patchLead({
        externalId,
        data: { mileage: Number(values.mileage) },
      });
      queryClient.setQueryData(getLeadByExternalIdQueryKey({ externalId }), updatedLead);
      onConfirm();
    } catch {
      setSaveError("We couldn't save that. Please try again.");
    }
  };

  const handleSubmit = async (
    values: OdometerMileageFormValues,
    helpers: FormikHelpers<OdometerMileageFormValues>,
  ) => {
    setSaveError(null);
    const baseline =
      queryClient.getQueryData<GetLeadByExternalId200>(getLeadByExternalIdQueryKey({ externalId }))
        ?.mileage ?? null;
    const mileage = Number(values.mileage);
    if (baseline != null && mileage < baseline && !confirmedBelowBaseline.current) {
      setShowBelowBaselineWarning(true);
      helpers.setFieldError(
        'mileage',
        `That's below the mileage we have on file (${formatMileage(
          baseline,
        )} mi). Double-check the dash.`,
      );
      return;
    }
    setShowBelowBaselineWarning(false);
    await save(values);
  };

  const handleClose = () => {
    setSaveError(null);
    setShowBelowBaselineWarning(false);
    onCancel();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Current odometer photo"
      titleFontSize="22px"
      titleFontWeight={700}
    >
      <Formik<OdometerMileageFormValues>
        initialValues={{ mileage: '' }}
        validationSchema={odometerSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={handleSubmit}
      >
        {({ submitForm, isSubmitting }) => (
          <Form>
            <VStack spacing={4} pb={4} align="stretch">
              <NumberInput
                name="mileage"
                label="Current mileage"
                helperText="Right off the dash, whole miles."
                isWhole
                showThousandSeparator
                autoFocus
              />
              {saveError && (
                <Text fontSize="sm" color="leaseEndRed" textAlign="center">
                  {saveError}
                </Text>
              )}
              <Center w="100%">
                <PrimaryButton type="submit" loading={isPending} value="SAVE MILEAGE" />
              </Center>
              {showBelowBaselineWarning && (
                <Center w="100%">
                  <Button
                    variant="link"
                    isDisabled={isPending || isSubmitting}
                    onClick={() => {
                      confirmedBelowBaseline.current = true;
                      submitForm().finally(() => {
                        confirmedBelowBaseline.current = false;
                      });
                    }}
                  >
                    Yes, I'm sure
                  </Button>
                </Center>
              )}
            </VStack>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default OdometerMileageModal;
